import React, { useCallback, useEffect, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import { api } from "../../../software/api";
import { Package } from "lucide-react";
import { money, CatBox, OrangeBar, barSelect, barBtn } from "./catalogShared";

function Barcode({ value }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value) {
      try { JsBarcode(ref.current, String(value), { format: "CODE128", displayValue: true, height: 42, fontSize: 12, margin: 0, width: 1.6 }); }
      catch { /* invalid barcode value */ }
    }
  }, [value]);
  if (!value) return <span className="text-[11px] text-slate-400">No barcode on file</span>;
  return <svg ref={ref} className="max-w-full" data-testid="part-barcode" />;
}

const partInp = "border border-[#c98f00] bg-white text-slate-900 px-2 py-1 text-[12px] rounded-sm outline-none focus:border-[#123a63] w-full";

export default function PartsSearch({ onExit, onNav }) {
  const [f, setF] = useState({ q: "", category: "", brand: "", part_id: "" });
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => { api("GET", "/parts/filters").then((r) => { setBrands(r?.brands || []); setCategories(r?.categories || []); }).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (f.q) qs.set("q", f.q);
    if (f.category) qs.set("category", f.category);
    if (f.brand) qs.set("brand", f.brand);
    if (f.part_id) qs.set("part_id", f.part_id);
    qs.set("limit", "60");
    try { setData(await api("GET", `/parts/search?${qs.toString()}`)); } catch { setData({ total: 0, items: [] }); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const clear = () => setF({ q: "", category: "", brand: "", part_id: "" });

  return (
    <div data-testid="parts-search-panel" className="rounded-xl overflow-hidden bg-white border border-[#c3d0e6]">
      <div className="flex items-center gap-2 bg-[#1a4f8a] px-3 py-1.5">
        <Package className="h-4 w-4 text-white" /><span className="text-white font-black text-sm">Parts Search</span>
      </div>

      <OrangeBar>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5 flex-1">
          <label className="text-[11px] font-black text-white">Part Description<input value={f.q} onChange={set("q")} data-testid="parts-f-desc" className={partInp} placeholder="Search description…" /></label>
          <label className="text-[11px] font-black text-white">Category
            <select value={f.category} onChange={set("category")} data-testid="parts-f-category" className={`${barSelect} w-full min-w-0`}><option value="">All</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</select>
          </label>
          <label className="text-[11px] font-black text-white">Brand
            <select value={f.brand} onChange={set("brand")} data-testid="parts-f-brand" className={`${barSelect} w-full min-w-0`}><option value="">All</option>{brands.map((b) => <option key={b} value={b}>{b}</option>)}</select>
          </label>
          <label className="text-[11px] font-black text-white">Part ID<input value={f.part_id} onChange={set("part_id")} data-testid="parts-f-partid" className={partInp} placeholder="Search Part ID…" /></label>
        </div>
        <div className="flex flex-col gap-1.5 ml-3">
          <button onClick={clear} data-testid="parts-clear" className={barBtn}>Clear Searches</button>
          {onNav && <button onClick={() => onNav("tab:stocktake")} data-testid="parts-view-stock" className={`${barBtn} !bg-emerald-600 !text-white hover:!bg-emerald-700`}>View Stock</button>}
          <button onClick={onExit} data-testid="parts-close-form" className={barBtn}>Close Form</button>
        </div>
      </OrangeBar>

      <div className="p-3">
        <p className="text-[11px] text-slate-500 mb-2" data-testid="parts-result-count">{loading ? "Searching parts…" : `Showing ${data.items.length} of ${data.total} matching part${data.total === 1 ? "" : "s"}.`}</p>
        <div className="divide-y-2 divide-slate-200">
          {data.items.map((p) => (
            <div key={p.id} data-testid="part-row" className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_0.7fr] gap-4 py-3 items-center">
              <div>
                <CatBox label="Part Description" value={p.name} labelW={112} />
                <CatBox label="Brand" value={p.brand} labelW={112} />
                <CatBox label="PartsID" value={p.sku} labelW={112} />
                <CatBox label="Dealer" value={p.supplier} labelW={112} />
                <div className="grid items-center gap-1.5 text-[12px] mb-1" style={{ gridTemplateColumns: "112px 1fr" }}>
                  <span className="text-slate-800 text-right font-semibold">Price:</span>
                  <span className="px-2 py-1 border border-[#b9b9b9] bg-white font-black text-[#123a63]">{money(p.price)}</span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-[12px] font-black text-slate-700">Barcode</span>
                <Barcode value={p.barcode || p.sku} />
              </div>
              <div>
                <div className="text-center mb-2">
                  <p className="text-[12px] font-black text-slate-700 leading-tight">Qty in<br />Stock</p>
                  <span className="inline-block mt-1 px-4 py-1.5 border border-[#b9b9b9] bg-white font-black text-[#123a63] min-w-[70px] text-center" data-testid="part-qty">{p.qty ?? 0}</span>
                </div>
                <div className="grid items-center gap-1.5 text-[12px]" style={{ gridTemplateColumns: "88px 1fr" }}>
                  <span className="text-slate-800 text-right font-semibold whitespace-nowrap">Stock Location:</span>
                  <span className="px-2 py-1 border border-[#b9b9b9] bg-white truncate min-h-[26px]">{p.location || ""}</span>
                </div>
              </div>
            </div>
          ))}
          {!loading && data.items.length === 0 && <p className="text-center text-slate-500 text-sm py-8">No parts match your search.</p>}
        </div>
        {!loading && data.total > data.items.length && <p className="text-[11px] text-slate-400 mt-3 text-center">Refine your search above to narrow down the remaining {data.total - data.items.length} parts.</p>}
      </div>
    </div>
  );
}
